import {
  ILocation,
  IOrder,
  IOrderMessage,
  IVehicle,
  OrderDirection,
} from '@boma/supplier-portal/shared/entities';
import { IOrderHandshakeOffer } from '@boma/website/shared/entities';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as OrdersActions from './orders.actions';

export const ORDERS_FEATURE_KEY = 'orders';

export interface OrdersState extends EntityState<IOrder> {
  selectedId?: string | number; // which Orders record has been selected
  loaded: boolean; // has the Orders list been loaded
  error?: string | null; // last known error (if any)
  addingOrUpdatingEvent: boolean;
  locationIds?: string[];
  customerIds?: string[];
  dateRange?: { fromDate: string; toDate: string };
  nextPage?: number;
  perPage: string;
  query?: string;
  locationType?: string;
  locations: ILocation[] | IVehicle[];
  orderDirection: OrderDirection;
  messages: IOrderMessage[] | null;
  statuses: string[];
  orderTypes: string[];
  handShake: IOrderHandshakeOffer | null;
}

export interface OrdersPartialState {
  readonly [ORDERS_FEATURE_KEY]: OrdersState;
}

export const ordersAdapter: EntityAdapter<IOrder> =
  createEntityAdapter<IOrder>();

export const ordersInitialState: OrdersState = ordersAdapter.getInitialState({
  // set initial required properties
  loaded: false,
  locationType: null,
  nextPage: 1,
  perPage: '25',
  locations: [],
  locationIds: [],
  customerIds: [],
  dateRange: null,
  addingOrUpdatingEvent: false,
  orderDirection: OrderDirection.Outbound,
  messages: null,
  statuses: ['Placed', 'Processing', 'Accepted', 'Loading', 'Dispatched'],
  orderTypes: ['Order'],
  handShake: null,
});

export const ordersReducer = createReducer(
  ordersInitialState,
  on(OrdersActions.loadOrders, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(
    OrdersActions.updateOrderSearchParams,
    (
      state,
      {
        query,
        locationIds,
        locationType,
        orderDirection,
        statuses,
        customerIds,
        dateRange,
        orderTypes,
      }
    ) =>
      ordersAdapter.removeAll({
        ...state,
        query,
        locationIds,
        locationType,
        nextPage: 1,
        loaded: false,
        orderDirection,
        statuses,
        customerIds,
        dateRange,
        orderTypes,
      })
  ),
  on(OrdersActions.loadOrdersSuccess, (state, { payload }) =>
    ordersAdapter.upsertMany(payload.items, {
      ...state,
      loaded: true,
      nextPage: payload.nextPage,
      markers: [{ long: 123, lat: 123 }],
    })
  ),
  on(OrdersActions.loadOrderDetails, (state) => ({
    ...state,
    loaded: false,
    messages: null,
    error: null,
  })),
  on(OrdersActions.loadOrderDetailsSuccess, (state, { order }) =>
    ordersAdapter.upsertOne(order, {
      ...state,
      selectedId: order.id,
      loaded: true,
      error: null,
    })
  ),
  on(OrdersActions.loadOrderDetailsFailure, (state, { error }) => ({
    ...state,

    error,
  })),
  on(OrdersActions.cancelFulfilledOrder, (state) => ({
    ...state,
    loaded: false,
  })),
  on(OrdersActions.loadOrderHandShake, (state) => ({
    ...state,
    error: null,
  })),
  on(
    OrdersActions.loadOrderHandShakeOfferSuccess,
    (state, { handShakeOffer }) => ({
      ...state,
      handShake: handShakeOffer,
    })
  ),
  on(OrdersActions.loadOrderHandShakeOfferFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  on(OrdersActions.loadOrdersFailure, (state, { error }) => ({
    ...state,
    error,
    loaded: true,
  })),
  on(OrdersActions.loadOrderLocationsSuccess, (state, { payload }) => ({
    ...state,
    locations: payload,
  })),
  on(OrdersActions.loadOrderMessagesSuccess, (state, { payload }) => {
    return {
      ...state,
      messages: payload,
    };
    //TODO Push these messages into the actual order entity or... save the order id so we can filter them.
    // let newMessageArray = [...(state.messages ?? [])].concat(payload);
    // newMessageArray = [
    //   ...new Map(newMessageArray.map((item) => [item.id, item])).values(),
    // ];
    // newMessageArray.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    // return {
    //   ...state,
    //   messages: [...newMessageArray],
    // };
  }),
  on(OrdersActions.createOrderMessageSuccess, (state, { message }) => {
    let newMessageArray = [...(state.messages ?? [])].concat([message]);
    newMessageArray = [
      ...new Map(newMessageArray.map((item) => [item.id, item])).values(),
    ];
    newMessageArray.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return {
      ...state,
      messages: [...newMessageArray],
    };
  })
);
